import { Global, css } from "@emotion/react"
import styled from "@emotion/styled"
import { VisuallyHidden } from "@react-aria/visually-hidden"
import { motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"

import { useDictionary } from "contexts/Dictionary"
import { useHeader } from "contexts/Header"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import { useViewport } from "contexts/Viewport"

import getMediaQuery from "css/breakpoints"
import { buttonFilled, buttonInline, green, opaqueGreen } from "css/buttons"
import { bold, headline50, legend100 } from "css/text"

import useSSR from "hooks/useSSR"

import { Icon } from "components/icon/Icon"
import Loader from "components/loader/Loader"
import { useEnv } from "contexts/Env"
import { waTriggerEvent } from "../analytics/DigitalDataLayer"
import AdvancedFilters from "./AdvancedFilters"
import BeautyshotCard from "./beautyshot-card/BeautyshotCard"
import { useWatchgrid } from "./context"
import EditoCard from "./edito-card/EditoCard"
import { getOriginLevel2, sendWatchGridEvent } from "./events"
import { gridTemplate } from "./gridTemplate"
import QuickFilters from "./QuickFilters"
import SelectedFilters from "./SelectedFilters"
import { getAllParams, getCurrentUrl, setParam } from "./utils"
import WatchCard from "./watch-card/WatchCard"

const MainStyles = css`
  main {
    z-index: unset;
  }
`
const Root = styled.div`
  --subnav-ofs: calc(var(--subnav-height) * var(--is-sub));
  margin-block-end: 100px;

  .page-title-block {
    position: unset;
    color: inherit;
    margin-block: calc(10vh + var(--subnav-ofs)) 50px;

    & > * {
      grid-column: 1/-1;
    }
  }
`
const Main = styled.section``
const PriceFilters = styled.div`
  width: calc(100vw - (2 * var(--outer-margin)));
  max-width: 120rem;
  margin-inline: auto;
  margin-block-end: 10px;
  position: relative;
  z-index: 5;
`
const PriceFiltersWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: end;

  button {
    background: none;
    border: none;
    cursor: pointer;
    ${legend100}
    padding: 0;
    color: rgb(var(--light-black));
  }

  .sort-button {
    padding-block: 5px;
  }

  .panel-open & {
    margin-inline-end: 25%;
  }
`
const SortButton = styled.button`
  display: flex;
  align-items: center;
  ${bold}
`
const SortIcon = styled(motion.span)`
  margin-inline-start: 5px;
  background: currentColor;
  height: 1em;
  mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"><path d="M7.5,11L0.9,4H7h7L7.5,11z"/></svg>');
  width: 1em;
  transition: background-color 0.3s ease-out;
`
const SortLabel = styled.span`
  font-weight: normal;
`
const PriceFiltersList = styled.ul`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1;
  background: rgb(var(--pure-white));
  padding: 10px 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.07);

  [dir="rtl"] & {
    right: auto;
    left: 0;
  }
`
const PriceFiltersItem = styled.li`
  margin-block-end: 5px;

  &:last-child {
    margin: 0;
  }
`
const PriceFiltersButton = styled.button`
  .active &,
  &:hover {
    color: rgb(var(--green));
  }
`

const Container = styled.div`
  position: relative;
`
const Wrapper = styled.div`
  display: grid;
  justify-content: center;
  grid-template-areas: "grid sidebar";
  padding-block-end: 190px;
  max-width: 120rem;

  ${getMediaQuery("l")} {
    width: calc(100vw - (2 * var(--outer-margin)));
    margin-inline: auto;
    overflow: hidden;

    .panel-open & {
      justify-content: end;
      overflow: visible;
      grid-template-columns: 3fr 1fr;
    }
  }
`
const GridContent = styled.div`
  grid-area: grid;
  width: var(--watches-grid-width);
  max-width: 120rem;

  ${getMediaQuery("l")} {
    .panel-open & {
      width: auto;
    }
  }
`
const GridList = styled.div`
  transition: opacity 0.3s;
`
const NoResult = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;

  span {
    ${headline50}
    color: rgb(var(--green));
    margin-block-end: 20px;
  }

  p {
    ${legend100}
  }
`
const List = styled.ul`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 1fr;
  margin-block-end: 10px;
  opacity: 0;
  transition-property: opacity;
  transition-duration: 0.5s;
  grid-auto-rows: auto;

  &.visible,
  .no-js & {
    opacity: 1;
  }

  &:last-of-type {
    margin-block-end: 0;
  }

  ${getMediaQuery("l")} {
    grid-auto-rows: 1fr;
  }

  ${getMediaQuery("xl")} {
    grid-template-columns: repeat(3, 1fr);

    .panel-open & {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  ${getMediaQuery("xxll")} {
    .panel-open & {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  li {
    & > a,
    & > button {
      overflow: hidden;

      &.focus-visible {
        outline-offset: -2px !important;
      }
    }

    img {
      transition: transform 0.6s ease;
    }

    @media (hover: hover) {
      &:hover {
        img {
          transform: scale(1.03);
        }
      }
    }
  }
`

const WatchCardListItem = styled.li`
  display: flex;
`

const CardItem = styled.li`
  display: flex;
  grid-column: span 2;
  width: 100%;
  /* height: 50vw; */ // really usefull ?

  > div {
    width: 100%;
  }

  ${getMediaQuery("m")} {
    width: 100%;
    height: 100%;
    grid-column: unset;
  }
`

const FilterButton = styled.button`
  ${buttonFilled}
  ${opaqueGreen}
  position: sticky;
  top: 100vh;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  transition: top 0.4s;

  [dir="rtl"] & {
    left: auto;
    right: 50%;
    transform: translateX(50%);
  }

  ${getMediaQuery("xl")} {
    top: 90vh;
  }
`
const LoaderWrapper = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  margin-block-start: 20px;

  .loader {
    margin-inline: auto;
  }
`
const ButtonWrapper = styled.div`
  position: absolute;
  bottom: 110px;
  left: 0;
  width: var(--watches-grid-width);
  height: 80px;
  margin-inline: var(--outer-margin);
  border-block-end: 1px solid rgb(var(--light-grey));
  display: grid;
  justify-items: center;
`
const LoadButton = styled.button`
  ${buttonInline}
  ${green}
`

const GRID_WATCHES_NUMBER = 14
const MAX_GRID_WATCHES_NUMBER = 18
const LOADING_WATCHES_NUMBER = 56
const MAX_LOADING_WATCHES_NUMBER = 72

let beautyIndex = -1
let editoIndex = -1

function CardList({ data, gridIndex = 0, beautyShots, editoCards, pricePopin, heading, searchUids, searchHubs, category, pipelines }) {
  const listRef = useRef()
  const env = useEnv()
  const locale = useLocale()

  const domain = process.browser ? `${global.location.protocol}//${global.location.hostname}` : process.env.REVERSE_PROXY_URL
  const lang = locale?.current?.codes?.www
  const countWatches = data.length
  const productCardState = beautyShots.length === 0 && editoCards.length === 0
  const gridWatchesNumber = productCardState ? MAX_GRID_WATCHES_NUMBER : GRID_WATCHES_NUMBER
  const gridVersion = productCardState ? 18 : countWatches === gridWatchesNumber ? (gridIndex % 2 == 0 ? 141 : 142) : countWatches
  const gridTemplateCurrent = gridTemplate[gridVersion] ? gridTemplate[gridVersion] : gridTemplate[18]

  let watchIndex = -1

  useEffect(() => {
    const listElement = listRef.current

    const observer = new IntersectionObserver(([{ isIntersecting }]) => {
      if (isIntersecting) {
        observer.unobserve(listElement)

        listElement.classList.add("visible")
      }
    })
    observer.observe(listElement)

    return () => {
      observer.unobserve(listElement)
    }
  }, [])

  const handleEvent = (watch, index, type) => {
    // console.log("🚀 ~ handleEvent ~ watch:", watch)
    const searchHub = searchHubs[type]
    const searchUid = searchUids[type]
    // console.log("🚀 ~ handleEvent ~ searchUid:", searchUid)
    if (searchUid)
      sendWatchGridEvent(env.organizationId, global.accessTokens.pipelineGrids, "click", {
        actionCause: "documentOpen",
        language: locale?.current?.codes?.www,
        queryText: "",
        responseTime: 0,
        documentPosition: index + 1,
        numberOfResults: gridWatchesNumber,
        userAgent: navigator?.userAgent,
        searchQueryUid: searchUid,
        searchHub,
        anonymous: true,
        originLevel1: searchHub,
        originLevel2: getOriginLevel2(),
        sourceName: watch.source ?? "rlxcom_beautyshots_index_2023_06",
        documentUriHash: watch.urihash,
        customData: {
          contentIDKey: "permanentid",
          contentIDValue: watch.permanentid ? watch.permanentid : watch.rmc,
        },
      })
  }

  const handleCardClick = (watch, index, filterCurrent, type) => {
    let language_code = lang != "en" ? lang : "" //no /en in the URL for master
    waTriggerEvent({
      eventName: "editoCardClicked",
      eventType: "Click",
      editoCardInfo: {
        name: type == "edito" ? watch?.cardId : watch?.rmc,
        url: type == "edito" ? domain + `/` + language_code + watch?.link?.href : "",
        category: category.current,
      },
    })
  }

  function caseBeautyshotCard(item, i) {
    beautyIndex = beautyIndex + 1 < beautyShots.length ? beautyIndex + 1 : 0
    const data = beautyShots[beautyIndex]

    function onClick(e = {}) {
      if (e.currentTarget && !e.currentTarget.contains(e.target)) return
      handleEvent(data, i, 1)
      handleCardClick(data, i, item, "beautyshot")
    }

    return (
      <CardItem
        key={`beautyshotcard_${i}`}
        //        onClick={onClick}
      >
        <BeautyshotCard key={i} {...data} onClick={onClick} />
      </CardItem>
    )
  }

  function caseEditoCard(item, i) {
    editoIndex = editoIndex + 1 < editoCards.length ? editoIndex + 1 : 0
    const data = editoCards[editoIndex]

    function onClick(e = {}) {
      if (e.currentTarget && !e.currentTarget.contains(e.target)) return
      handleEvent(data, i, 2)
      handleCardClick(data, i, item, "edito")
    }

    return (
      <CardItem key={`editocard_${i}`} onClick={onClick}>
        <EditoCard key={i} {...data} />
      </CardItem>
    )
  }

  return (
    <List ref={listRef} aria-label={heading.title}>
      {gridTemplateCurrent.map((item, i) => {
        switch (item) {
          case "watch":
            watchIndex += 1
            return data[watchIndex] ? (
              <WatchCardListItem key={`card_${i}`} onClick={() => handleEvent(data[watchIndex], i, 0)}>
                <WatchCard {...data[watchIndex]} key={data[watchIndex].rmc} pricePopin={pricePopin} />
              </WatchCardListItem>
            ) : null
          case "beauty":
            if (beautyShots.length > 0) {
              return caseBeautyshotCard(item, i)
            } else {
              return caseEditoCard(item, i)
            }
          case "edito":
            if (editoCards.length > 0) {
              return caseEditoCard(item, i)
            } else {
              return caseBeautyshotCard(item, i)
            }
        }
      })}
    </List>
  )
}

export default function WatchGrid({
  totalCount,
  results,
  gridFilters,
  beautyShots,
  editoCards,
  pricePopin,
  watchgridURL,
  searchUidWatches,
  searchHubWatches,
  searchUidBeautyShots,
  searchHubBeautyshots,
  searchUidEditocards,
  searchHubEditocards,
}) {
  const [loaderDisplay, setLoaderDisplay] = useState(false)
  const [gridDisplay, setGridDisplay] = useState(true)
  const [fadeIn, setFadeIn] = useState(true)

  const [sortPanel, setSortPanel] = useState(false)
  const [sortFilter, setSortFilter] = useState(null)

  beautyIndex = -1
  editoIndex = -1

  const { setAdvancedPanel, advancedPanel, filterStrip, setFilterStrip } = useWatchgrid()

  const dictionary = useDictionary()
  const ssr = useSSR()
  const header = useHeader()
  const location = useLocation()
  const env = useEnv()
  const locale = useLocale()
  const navigation = useNavigation()
  const viewport = useViewport()
  const { isMobile } = viewport

  const mainRef = useRef()
  const filterButtonRef = useRef()

  const beautyshotsData = useRef(beautyShots)
  const editocardsData = useRef(editoCards)
  const pricepopinData = useRef(pricePopin)
  const filtersData = useRef(gridFilters)
  const loadingCount = useRef(0)
  let productCardState = beautyShots.length === 0 && editoCards.length === 0
  const gridWatchesNumberRef = useRef(productCardState ? MAX_GRID_WATCHES_NUMBER : GRID_WATCHES_NUMBER)
  const loadingWatchesNumberRef = useRef(productCardState ? MAX_LOADING_WATCHES_NUMBER : LOADING_WATCHES_NUMBER)
  const maxLoadingCount = useRef(Number.parseInt(totalCount / loadingWatchesNumberRef.current))
  const gridData = useRef(productCardState ? results : results.slice(0, LOADING_WATCHES_NUMBER))
  const watchesCount = useRef(gridData.current.length)
  const category = useRef(null)

  const countCards = useRef(0)

  const searchUids = [searchUidWatches]
  const searchHubs = [searchHubWatches, searchUidBeautyShots, searchHubEditocards]
  const pipelines = [global.accessTokens.pipelineCoveoPLP, global.accessTokens.pipelineGrids, global.accessTokens.pipelineGrids]

  const { quick_filters: QFData, advanced_filters: AFData } = gridFilters

  async function loadContent() {
    loadingCount.current++

    const urlParams = watchgridURL.searchParams

    const currentParams = getAllParams()

    for (const facet in currentParams) {
      const paramValue = facet === "sort" ? currentParams[facet].toString() : `(${currentParams[facet].toString()})`

      urlParams.set(facet, paramValue)
    }

    watchgridURL.searchParams.set("numberOfResults", MAX_LOADING_WATCHES_NUMBER * (loadingCount.current + 1))
    const response = await fetch(watchgridURL)
    const jsonData = await response.json()
    filtersData.current = jsonData.gridFilters
    const results = jsonData.results
    beautyshotsData.current = jsonData.beautyShots
    editocardsData.current = jsonData.editoCards
    pricepopinData.current = jsonData.pricePopin
    productCardState = jsonData.beautyShots.length === 0
    gridWatchesNumberRef.current = productCardState ? MAX_GRID_WATCHES_NUMBER : GRID_WATCHES_NUMBER
    loadingWatchesNumberRef.current = productCardState ? MAX_LOADING_WATCHES_NUMBER : LOADING_WATCHES_NUMBER
    maxLoadingCount.current = Number.parseInt(jsonData.totalCount / loadingWatchesNumberRef.current)
    gridData.current = productCardState ? results : results.slice(0, LOADING_WATCHES_NUMBER * (loadingCount.current + 1))
    watchesCount.current = gridData.current.length

    setLoaderDisplay(false)
    setGridDisplay(true)
    setFadeIn(true)
  }

  function generateContent() {
    const gridNumber = Math.ceil(watchesCount.current / gridWatchesNumberRef.current)
    const content = []

    for (let i = 0; i < gridNumber; i++) {
      const start = gridWatchesNumberRef.current * i
      const defaultEnd = start + gridWatchesNumberRef.current
      const end = defaultEnd > watchesCount.current ? start + (watchesCount.current - start) : defaultEnd
      const data = gridData.current.slice(start, end)
      countCards.current += data.length

      content.push(
        <CardList
          key={i}
          data={data}
          gridIndex={i}
          beautyShots={beautyshotsData.current}
          editoCards={editocardsData.current}
          pricePopin={pricepopinData.current}
          heading={filtersData.current.heading}
          quickFilters={filtersData.current.quick_filters}
          filtersData={filtersData}
          searchUids={searchUids}
          searchHubs={searchHubs}
          category={category}
        />
      )
    }

    return content
  }

  useEffect(() => {
    setFadeIn(false)

    setTimeout(() => {
      setLoaderDisplay(true)
      setGridDisplay(false)

      updateSortFilter()

      countCards.current = 0

      loadingCount.current = -1

      loadContent()

      if (global?.scrollY !== 0) {
        header.freeze("watchgrid")

        const headerHeight = header.expansion()
          ? document.querySelector("header").clientHeight
          : 0 + document.querySelector(".header-subnav")
          ? document.querySelector(".header-subnav").clientHeight
          : 0
        const top = mainRef.current.offsetTop - headerHeight
        global?.scrollTo(0, top)

        global?.requestAnimationFrame(() => {
          header.unfreeze("watchgrid")
        })
      }
    }, 300)
  }, [location])

  useEffect(() => {
    updateSortFilter()

    const currentParams = getAllParams()

    const currentUrl = getCurrentUrl()

    if (
      (Object.keys(currentParams).length === 1 &&
        Object.keys(currentParams)[0] !== filtersData?.current?.quick_filters[0]?.facet &&
        !currentUrl.searchParams.get("sort") &&
        !currentUrl.searchParams.get("group")) ||
      (Object.keys(currentParams).length > 1 && !currentUrl.searchParams.get("sort"))
    )
      setFilterStrip(true)

    // coveo events
    if (searchUids.length > 0 && searchHubs.length > 0) {
      searchUids.forEach((searchUid, index) => {
        const token = pipelines[index]
        console.log("🚀 ~ searchUids.forEach ~ token:", token)

        sendWatchGridEvent(env.organizationId, token, "search", {
          actionCause: "interfaceLoad",
          language: locale?.current?.codes?.www,
          queryText: "",
          responseTime: 0,
          numberOfResults: beautyshotsData.current.length,
          userAgent: navigator?.userAgent,
          searchQueryUid: searchUid,
          searchHub: searchHubs[index],
          anonymous: true,
          originLevel1: searchHubs[index],
          originLevel2: getOriginLevel2(currentParams?.global_category?.[0]),
        })
      })
    }

    document.body.addEventListener("click", closeSortPanel)

    return () => {
      setFilterStrip(false)
      document.body.removeEventListener("click", closeSortPanel)
    }
  }, [])

  function closeSortPanel(e) {
    if (e.target.classList.contains("sort-element")) return

    setSortPanel(false)
  }

  function updateSortFilter() {
    const currentParams = getAllParams()

    const currentUrl = getCurrentUrl()

    const sortbyValues = filtersData.current.advanced_filters[0].values

    for (const facet in currentParams) {
      if (facet === "sort") {
        Object.entries(sortbyValues).map(item => {
          if (currentParams[facet][0] === item[1].value) setSortFilter(item[1].localizedValue)
        })
      }
    }

    if (!currentUrl.searchParams.get("sort")) setSortFilter(sortbyValues[0].localizedValue)
  }

  function loadMore() {
    setLoaderDisplay(true)

    loadContent()
  }

  function filtersPanelHandle() {
    setSortPanel(false)

    mainRef.current.classList.toggle("panel-open")

    setAdvancedPanel(!advancedPanel)

    if (advancedPanel) {
      filterButtonRef.current.focus()
    } else {
      mainRef.current.querySelector(".advancedFiltersReset")?.focus()
    }
  }

  function sortPanelHandle(localizedValue, name, value) {
    if (localizedValue) setSortFilter(localizedValue)
    setSortPanel(!sortPanel)

    if (value) {
      const currentUrl = getCurrentUrl()

      const params = currentUrl.searchParams

      setParam(name, value, params)

      navigation.push(currentUrl.href, { nopageview: true }, { scroll: false })
    }
  }

  const onResize = () => {
    if (!filterButtonRef.current) return
    filterButtonRef.current.style.top = `calc(${window.innerHeight}px - ${isMobile.current ? "64px" : "68px"})`
  }

  const toggleVariants = {
    hide: { rotate: 0, transition: { duration: 0.35, ease: "linear" } },
    show: { rotate: 180, transition: { duration: 0.35, ease: "linear" } },
  }

  useEffect(() => {
    if (gridData.current.length > 0 && AFData?.length > 0 && process.browser) {
      onResize()

      window.addEventListener("resize", onResize)
    }
  })

  return (
    <>
      <Global styles={MainStyles}></Global>
      <Main ref={mainRef}>
        {QFData?.length > 0 &&
          (filterStrip ? (
            <SelectedFilters
              advancedFilters={filtersData.current.advanced_filters}
              quickFilters={filtersData.current.quick_filters}
              searchHubs={searchHubs}
              pipelines={pipelines}
              searchUids={searchUids}
              gridData={gridData}
            />
          ) : (
            <QuickFilters
              quickFilters={filtersData.current.quick_filters}
              searchHubs={searchHubs}
              pipelines={pipelines}
              searchUids={searchUids}
              gridData={gridData}
              category={category}
            />
          ))}

        <PriceFilters>
          <PriceFiltersWrapper>
            <SortButton className='sort-button sort-element' onClick={() => sortPanelHandle()}>
              {dictionary.sortBy()}
              {!sortFilter ? "" : ": "}
              {!sortFilter ? "" : <SortLabel className='sort-element' dangerouslySetInnerHTML={{ __html: `&nbsp;${sortFilter}` }} />}
              <SortIcon className='sort-element' initial='hide' animate={sortPanel ? "show" : "hide"} variants={toggleVariants} />
            </SortButton>

            <PriceFiltersList style={{ display: sortPanel ? "block" : "none" }}>
              {filtersData.current.advanced_filters[0].values.map((item, i) => {
                return (
                  <PriceFiltersItem key={i} className={item.localizedValue === sortFilter ? "active" : ""}>
                    <PriceFiltersButton onClick={() => sortPanelHandle(item.localizedValue, item.facet, item.value)}>{item.localizedValue}</PriceFiltersButton>
                  </PriceFiltersItem>
                )
              })}
            </PriceFiltersList>
          </PriceFiltersWrapper>
        </PriceFilters>

        <Container style={{ marginBlockStart: gridData.current.length !== 0 && AFData?.length > 1 ? "-44px" : null }}>
          {gridData.current.length > 0 && AFData?.length > 1 && (
            <FilterButton ref={filterButtonRef} onClick={filtersPanelHandle}>
              <Icon type='filters' />
              {dictionary.filters()}
            </FilterButton>
          )}

          <Wrapper>
            <VisuallyHidden aria-live='polite'>
              {countCards.current !== 0 && (
                <span>
                  {countCards.current} {dictionary.results()}
                </span>
              )}
            </VisuallyHidden>

            <GridContent>
              {gridDisplay &&
                (gridData.current.length === 0 ? (
                  <NoResult>
                    <span>{dictionary.noResultTitle()}</span>
                    <p>{dictionary.noResultText()}</p>
                  </NoResult>
                ) : (
                  <GridList style={{ opacity: fadeIn ? 1 : 0 }}>{generateContent()}</GridList>
                ))}

              {loaderDisplay && (
                <LoaderWrapper>
                  <Loader />
                </LoaderWrapper>
              )}
            </GridContent>

            {AFData?.length > 1 && (
              <AdvancedFilters
                searchHubs={searchHubs}
                pipelines={pipelines}
                searchUids={searchUids}
                gridData={gridData.current}
                gridFilters={filtersData.current}
                closeMethod={filtersPanelHandle}
              />
            )}
          </Wrapper>

          {!loaderDisplay && gridData.current.length > 0 && loadingCount.current < maxLoadingCount.current && (
            <ButtonWrapper>
              <LoadButton onClick={loadMore}>{dictionary.loadMore()}</LoadButton>
            </ButtonWrapper>
          )}
        </Container>
      </Main>
    </>
  )
}
